import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, TextField, Button, Alert, Divider,
  Table, TableHead, TableBody, TableRow, TableCell,
  Chip, MenuItem, Select, FormControl, IconButton, Tooltip, List, ListItem, ListItemText,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import apiClient from '../api/client';

const ORG_ROLES = ['owner', 'admin', 'member'];

export default function OrgSettings({ user }) {
  const canEdit = ['owner', 'admin'].includes(user?.org_role);
  const [org, setOrg] = useState(null);
  const [form, setForm] = useState({ name: '', contact_email: '', phone: '', address: '' });
  const [members, setMembers] = useState([]);
  const [saveMsg, setSaveMsg] = useState('');
  const [addForm, setAddForm] = useState({ email: '', role: 'member' });
  const [addError, setAddError] = useState('');

  const load = useCallback(async () => {
    const [o, m] = await Promise.all([
      apiClient.get('/org'),
      apiClient.get('/org/members'),
    ]);
    setOrg(o.data.data);
    setForm({
      name: o.data.data.name || '',
      contact_email: o.data.data.contact_email || '',
      phone: o.data.data.phone || '',
      address: o.data.data.address || '',
    });
    setMembers(m.data.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveOrg = async (e) => {
    e.preventDefault();
    await apiClient.put('/org', form);
    setSaveMsg('Saved');
    setTimeout(() => setSaveMsg(''), 2000);
  };

  const updateRole = async (memberId, role) => {
    try {
      await apiClient.put(`/org/members/${memberId}`, { role });
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update role');
    }
  };

  const removeMember = async (memberId) => {
    try {
      await apiClient.delete(`/org/members/${memberId}`);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to remove member');
    }
  };

  const addMember = async () => {
    setAddError('');
    try {
      await apiClient.post('/org/members', addForm);
      setAddForm({ email: '', role: 'member' });
      load();
    } catch (err) {
      setAddError(err.response?.data?.error || 'Failed to add member');
    }
  };

  if (!org) return null;

  return (
    <Box>
      <Typography variant="h4" fontWeight={700} mb={4}>Organization Settings</Typography>

      {/* Details */}
      <Paper sx={{ p: 3, mb: 4, maxWidth: 560 }}>
        <Typography variant="h6" fontWeight={600} mb={2}>Details</Typography>
        <Box component="form" onSubmit={saveOrg} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField label="Name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required size="small" InputProps={{ readOnly: !canEdit }} />
          <TextField label="Contact Email" value={form.contact_email} onChange={e => setForm(f => ({ ...f, contact_email: e.target.value }))} size="small" InputProps={{ readOnly: !canEdit }} />
          <TextField label="Phone" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} size="small" InputProps={{ readOnly: !canEdit }} />
          <TextField label="Address" value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} size="small" InputProps={{ readOnly: !canEdit }} />
          {canEdit && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button type="submit" variant="contained" size="small">Save</Button>
              {saveMsg && <Typography variant="body2" color="success.main">{saveMsg}</Typography>}
            </Box>
          )}
        </Box>
      </Paper>

      {/* Members */}
      <Typography variant="h6" fontWeight={600} mb={2}>Members</Typography>
      <Paper sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell align="right"></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {members.map(m => {
              const isMe = m.user?.id === user?.id;
              const canEditRole = !isMe && (user?.org_role === 'owner' || (user?.org_role === 'admin' && m.role !== 'owner'));
              const canRemove = !isMe && (user?.org_role === 'owner' || (user?.org_role === 'admin' && m.role !== 'owner'));
              return (
                <TableRow key={m.id} hover>
                  <TableCell>
                    {m.user?.first_name} {m.user?.last_name}
                    {isMe && <Chip label="you" size="small" sx={{ ml: 1 }} />}
                  </TableCell>
                  <TableCell>{m.user?.email}</TableCell>
                  <TableCell>
                    {canEditRole ? (
                      <FormControl size="small" variant="standard">
                        <Select value={m.role} onChange={e => updateRole(m.id, e.target.value)} disableUnderline>
                          {ORG_ROLES
                            .filter(r => user?.org_role === 'owner' || r === 'member')
                            .map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                        </Select>
                      </FormControl>
                    ) : (
                      <Chip label={m.role} size="small" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {canRemove && (
                      <Tooltip title="Remove">
                        <IconButton size="small" color="error" onClick={() => removeMember(m.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>

      {canEdit && (
        <>
          <Typography variant="subtitle2" mb={1}>Add member</Typography>
          {addError && <Alert severity="error" sx={{ mb: 1 }}>{addError}</Alert>}
          <Box sx={{ display: 'flex', gap: 1, maxWidth: 500 }}>
            <TextField
              label="Email"
              size="small"
              value={addForm.email}
              onChange={e => setAddForm(f => ({ ...f, email: e.target.value }))}
              sx={{ flex: 1 }}
            />
            <FormControl size="small" sx={{ minWidth: 110 }}>
              <Select value={addForm.role} onChange={e => setAddForm(f => ({ ...f, role: e.target.value }))}>
                {ORG_ROLES
                  .filter(r => user?.org_role === 'owner' || r === 'member')
                  .map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
              </Select>
            </FormControl>
            <Button variant="contained" size="small" onClick={addMember} disabled={!addForm.email}>Add</Button>
          </Box>
        </>
      )}
    </Box>
  );
}
